import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Title3,
  Body1,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Spinner,
  Dropdown,
  Option,
  Button,
  MessageBar,
  MessageBarBody,
  Tooltip,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ArrowDownloadRegular,
  WarningRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { DATETIME_FORMAT } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  section: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  statsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  statCard: {
    padding: tokens.spacingVerticalM,
    minWidth: '150px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  warningBadge: {
    backgroundColor: '#fef3cd',
    color: '#856404',
  },
  dangerBadge: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  infoNote: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

interface UnusedAccount {
  id: string;
  displayName: string;
  email: string;
  userPrincipalName: string;
  lastSignIn: string | null;
  createdDateTime: string;
  accountEnabled: boolean;
  daysInactive: number;
}

interface UnusedAccountsResponse {
  success: boolean;
  data: {
    accounts: UnusedAccount[];
    count: number;
    thresholdDays: number;
  };
}

export function AuditPage() {
  const styles = useStyles();
  const { isLoading: authLoading } = useAuth();
  const [accounts, setAccounts] = useState<UnusedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thresholdDays, setThresholdDays] = useState<string>('30');

  const fetchUnusedAccounts = async (days: string) => {
    if (authLoading) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<UnusedAccountsResponse>(
        `/audit/unused-accounts?days=${days}`
      );
      setAccounts(response.data?.accounts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unused accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnusedAccounts(thresholdDays);
  }, [thresholdDays, authLoading]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', DATETIME_FORMAT);
  };

  const getDaysInactiveBadge = (days: number) => {
    if (days > 90) {
      return <Badge color="danger">{days} days</Badge>;
    } else if (days > 60) {
      return <Badge color="warning">{days} days</Badge>;
    } else {
      return <Badge color="subtle">{days} days</Badge>;
    }
  };

  const exportToCsv = () => {
    const headers = ['Display Name', 'Email', 'User Principal Name', 'Last Sign-In', 'Days Inactive', 'Created'];
    const rows = accounts.map(account => [
      account.displayName,
      account.email,
      account.userPrincipalName,
      account.lastSignIn || 'Never',
      account.daysInactive.toString(),
      account.createdDateTime,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `unused-accounts-${thresholdDays}days-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const criticalCount = (accounts || []).filter(a => a.daysInactive > 90).length;
  const warningCount = (accounts || []).filter(a => a.daysInactive > 60 && a.daysInactive <= 90).length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Audit</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Security audits and compliance reports
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Unused Accounts Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Title3>Unused Accounts</Title3>
          <Button
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={exportToCsv}
            disabled={loading || accounts.length === 0}
          >
            Export CSV
          </Button>
        </div>

        <div className={styles.infoNote}>
          <InfoRegular />
          <span>
            Shows accounts that haven't signed in within the selected period.
            Requires Azure AD Premium P1/P2 for sign-in activity data.
          </span>
        </div>

        <div className={styles.filters}>
          <Body1>Show accounts inactive for more than:</Body1>
          <Dropdown
            value={`${thresholdDays} days`}
            onOptionSelect={(_, data) => {
              setThresholdDays(data.optionValue as string);
            }}
            style={{ minWidth: '120px' }}
          >
            <Option value="30">30 days</Option>
            <Option value="60">60 days</Option>
            <Option value="90">90 days</Option>
            <Option value="180">180 days</Option>
          </Dropdown>
        </div>

        {!loading && accounts.length > 0 && (
          <div className={styles.statsRow}>
            <Card className={styles.statCard}>
              <div className={styles.statValue}>{accounts.length}</div>
              <div className={styles.statLabel}>Total Unused</div>
            </Card>
            {criticalCount > 0 && (
              <Card className={styles.statCard}>
                <div className={styles.statValue} style={{ color: '#c50f1f' }}>{criticalCount}</div>
                <div className={styles.statLabel}>90+ Days</div>
              </Card>
            )}
            {warningCount > 0 && (
              <Card className={styles.statCard}>
                <div className={styles.statValue} style={{ color: '#bc8b00' }}>{warningCount}</div>
                <div className={styles.statLabel}>60-90 Days</div>
              </Card>
            )}
          </div>
        )}

        {loading ? (
          <div className={styles.loading}>
            <Spinner size="large" label="Loading unused accounts..." />
          </div>
        ) : accounts.length === 0 ? (
          <Card className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
            <Body1>No unused accounts found for the selected threshold</Body1>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Display Name</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Last Sign-In</TableHeaderCell>
                  <TableHeaderCell>Days Inactive</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                        {account.daysInactive > 90 && (
                          <Tooltip content="Inactive for over 90 days" relationship="label">
                            <WarningRegular style={{ color: '#c50f1f' }} />
                          </Tooltip>
                        )}
                        {account.displayName}
                      </div>
                    </TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>{formatDate(account.lastSignIn)}</TableCell>
                    <TableCell>{getDaysInactiveBadge(account.daysInactive)}</TableCell>
                    <TableCell>{formatDate(account.createdDateTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
}
